// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// Pure field-resolution + filter predicates for the dashboard metrics. Deliberately NO Esri imports
// (only a layer's `fields` name array is read), so this logic is unit-testable in jsdom while the
// widgets recompute against the live layers in dashboardMetrics, which re-exports these.

// Normalize a field name for fuzzy matching: lowercase, strip everything but letters/digits.
export const normalizeFieldName = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]/g, '');

// Published GeoJSON layers rename gdb fields (e.g. Height_ft -> "Height (Ft)", Pole_ID -> "Pole ID").
// Resolve a field by fuzzy-matching candidate names against a layer's ACTUAL field names, ignoring
// case and punctuation; falls back to the first candidate if nothing matches.
export const resolveField = (
  layer: { fields?: { name: string }[] } | undefined,
  ...candidates: string[]
): string => {
  const wanted = candidates.map(normalizeFieldName);
  const match = (layer?.fields || []).find((field) => wanted.includes(normalizeFieldName(field.name)));
  return match ? match.name : candidates[0];
};

// A pinned scalar-KPI value (count / length / area / average / …) can come from the Lambda's whole-gdb
// snapshot (what Khora reported in chat) or a live recompute against the published viewer layer. At
// whole-gdb scope ("current view only" OFF) the published layer can diverge from the gdb, so trust the
// snapshot; in view-only mode always recompute (the snapshot is a whole-gdb figure). Type guard, so the
// caller can short-circuit the layer query and use the narrowed snapshot value directly.
export const preferSnapshot = (
  snapshotValue: number | undefined,
  viewOnly: boolean,
): snapshotValue is number => !viewOnly && typeof snapshotValue === 'number';

export type FilterOp = '<' | '<=' | '>' | '>=' | '=' | '!=';

// Does one attribute value pass a widget filter? Numeric filters coerce the attribute (a renamed field
// can publish numbers as strings); string filters compare lexically for =/!= and the ordering ops.
export const matchesFilter = (raw: unknown, op: FilterOp, value: number | string): boolean => {
  if (raw === null || raw === undefined) return false;
  if (typeof value === 'number') {
    const num = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isNaN(num)) return false;
    if (op === '<') return num < value;
    if (op === '<=') return num <= value;
    if (op === '>') return num > value;
    if (op === '>=') return num >= value;
    if (op === '=') return num === value;
    return num !== value;
  }
  const str = String(raw);
  if (op === '=') return str === value;
  if (op === '!=') return str !== value;
  if (op === '<') return str < value;
  if (op === '<=') return str <= value;
  if (op === '>') return str > value;
  return str >= value;
};
